<script>
import formService from "@/services/form";
import medicalSpecialtyService from "@/services/medical-specialty";
import { mapGetters } from "vuex";

export default {
  name: "FormSelect",
  props: {
    value: { type: String },
    readonly: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    completedForms: { type: Array, default: () => [] },
  },

  data() {
    return {
      forms: [],
      medicalSpecialties: [],
      isLoading: false,
    };
  },

  computed: {
    ...mapGetters(["user"]),

    findFormById() {
      const form = this.forms.find((elem) => elem._id === this.value);
      return form?.name || form?.title || form?.description || "Sin datos";
    },

    groupedForms() {
      return this.createFormGroups();
    },
  },

  async created() {
    await Promise.all([this.getForms(), this.getMedicalSpecialties()]);
  },

  methods: {
    async getForms() {
      this.isLoading = true;
      try {
        this.forms = await formService.getForms();
      } catch (error) {
        console.error("Error al cargar formularios:", error);
        this.forms = [];
      }
      this.isLoading = false;
    },

    async getMedicalSpecialties() {
      this.isLoading = true;
      try {
        this.medicalSpecialties =
          await medicalSpecialtyService.getMedicalSpecialties();
      } catch (error) {
        console.error("Error al cargar especialidades médicas:", error);
        this.medicalSpecialties = [];
      }
      this.isLoading = false;
    },

    onInput(value) {
      this.$emit("input", value);
      const selectedForm = this.forms.find((form) => form._id === value);
      if (selectedForm) {
        this.$emit("form-selected", selectedForm);
      }
    },

    onEditCompletedForm(completedForm) {
      this.$emit("edit-completed-form", completedForm);
    },

    onRemoveCompletedForm(completedForm) {
      this.$emit("remove-completed-form", completedForm);
    },

    createFormGroups() {
      if (!this.forms.length || !this.medicalSpecialties.length) {
        return [{ label: "Formularios", options: this.forms }];
      }

      const userMedicalSpecialtyIds =
        this.user?.doctor?.medicalSpecialties || [];
      const groups = [];
      const specialtyGroups = {};

      userMedicalSpecialtyIds.forEach((specialtyId) => {
        const specialty = this.medicalSpecialties.find(
          (s) => s._id === specialtyId
        );
        if (specialty) {
          specialtyGroups[specialtyId] = {
            label: specialty.name,
            options: [],
          };
        }
      });

      this.forms.forEach((form) => {
        if (form.medicalSpecialties && form.medicalSpecialties.length > 0) {
          let addedToSpecialty = false;

          form.medicalSpecialties.forEach((formSpecialtyId) => {
            const specialtyId = formSpecialtyId._id || formSpecialtyId;
            if (specialtyGroups[specialtyId]) {
              specialtyGroups[specialtyId].options.push(form);
              addedToSpecialty = true;
            }
          });

          if (!addedToSpecialty) {
            if (!specialtyGroups.others) {
              specialtyGroups.others = {
                label: "Otros",
                options: [],
              };
            }
            specialtyGroups.others.options.push(form);
          }
        } else {
          if (!specialtyGroups.others) {
            specialtyGroups.others = {
              label: "Otros",
              options: [],
            };
          }
          specialtyGroups.others.options.push(form);
        }
      });

      Object.values(specialtyGroups).forEach((group) => {
        if (group.options.length > 0) {
          groups.push(group);
        }
      });

      return groups;
    },
  },
};
</script>

<template lang="pug">
div
  el-select(
    v-if="!readonly"
    :value="value"
    @input="onInput"
    :loading="isLoading"
    :disabled="isDisabled"
    placeholder="Agregar formulario"
  )
    template(v-for="group in groupedForms")
      el-option(
        v-if="groupedForms.length > 1"
        :key="`group-${group.label}`"
        :value="`group-${group.label}`"
        :label="`${group.label}`"
        disabled
        style="cursor: default;"
      )
      el-option(
        v-for="form in group.options"
        :key="form._id"
        :label="form.name || form.title || form.description"
        :value="form._id"
      )

  el-input(
    v-else
    :value="findFormById"
    readonly
  )

  .completed-forms(v-if="completedForms.length > 0 && !readonly")
    .completed-form(
      v-for="(completedForm, index) in completedForms"
      :key="completedForm._id || index"
    )
      .form-info
        span.form-name {{ completedForm.name || completedForm.form?.name }}
        span.form-date {{ completedForm.createdAt | formatDateAndTime }}
      .form-actions
        .sent-indicator(v-if="completedForm.isSent")
          i.el-icon-check.sent-icon
          span.sent-text Enviado
        .form-buttons(v-else)
          button.button.button--blue(
            @click="onEditCompletedForm(completedForm)"
            :disabled="isDisabled"
          )
            i.el-icon-edit
          button.button.button--red(
            @click="onRemoveCompletedForm(completedForm)"
            :disabled="isDisabled"
          )
            i.el-icon-delete
</template>

<style scoped>
.completed-forms {
  margin-top: 10px;
}

.completed-form {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  margin-bottom: 8px;
  background-color: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.form-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.form-name {
  color: #303133;
  font-size: 14px;
}

.form-date {
  font-size: 12px;
  color: #909399;
}

.form-actions {
  display: flex;
  gap: 8px;
}

.sent-indicator {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background-color: #f0f9ff;
  border-radius: 4px;
  border: 1px solid #67c23a;
}

.form-buttons {
  display: flex;
  gap: 8px;
}

.button i {
  font-size: 16px;
  line-height: 1;
  vertical-align: middle;
  margin: 0;
  padding: 0;
  transition: transform 0.175s linear;
}

.button:hover i {
  transform: rotate(90deg);
}

.sent-icon {
  color: #67c23a;
  font-size: 18px;
}

.sent-text {
  color: #67c23a;
  font-size: 12px;
  margin-left: 4px;
}
</style>
